import { createAsyncThunk } from '@reduxjs/toolkit';

import { ISubmission } from '.';
import { APIDataProvider, TApiResponse, ApiPaths } from '../../api';
import { IQuestion } from '../QuestionSlice';
import { ISubTopic } from '../SubTopicSlice';
import { IQuiz } from '../QuizSlice';

export interface IGetAllSubmissions {
  limit?: number;
  offset?: number;
  quizId: string;
}
export interface ICreateNewSubmission {
  subTopicId: string;
  paper: string;
}
export interface ICreateNewSubmissionByQuizId {
  subTopicId: string;
  quizId: string;
  homeworkId?: string;
  paper?: string;
}
export interface ISubmissionCreateAnswer {
  submissionId: string;
  questionId: string;
  answerId: string;
}
export interface ISubmissionFinish {
  submissionId: string;
}
export interface ISubmissionGetQuestion {
  submissionId: string;
  questionId: string;
}
export interface ISubmissionGetQuestionAnswer {
  submissionId: string;
  questionId: string;
}
export interface IGetSubmissionsQuizHistory {
  limit: number;
  offset: number;
  quizId: string;
}

export interface ISubmissionUpdateCountdownSettings {
  rememberOption: boolean;
  submissionId: string;
  isContinueSubmissionWithTimerEnabled: boolean;
}

export interface IUpdateQuestionTimer {
  submissionId: string;
  questionId: string;
}

export interface IUpdateSubmissionQuestionOpenAiFeedback {
  submissionId: string;
  questionId: string;
  feedback: -1 | 0 | 1;
}

export interface ISubmissionCreateOpenEndedAnswer {
  submissionId: string;
  questionId: string;
  openEndedAnswer?: string;
  imageUrl?: string;
}

export interface ISubmissionGradeOpenEndedAnswer {
  submissionId: string;
  questionId: string;
  score?: number;
  comment?: string;
}

export interface IGetAiHint {
  questionId: string;
}

export interface ISubmissionCreateSubQuestionAnswer {
  submissionId: string;
  questionId: string;
  subQuestionId: string;
  answer?: string;
  imageUrl?: string;
}

const apiDataProvider = new APIDataProvider();

const getSubmissionListModule = async (data: IGetAllSubmissions) => {
  let path = `${ApiPaths.submissions}?quizId=${data.quizId}`;

  if (data.limit) {
    path += `&limit=${data.limit}`;
  }
  if (data.offset) {
    path += `&offset=${data.offset}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<{
      limit: number;
      offset: number;
      totalDocs: number;
      docs: Array<ISubmission>;
    }>
  >(path);

  return response.data;
};

const getSubmissionsForQuizHistoryListModule = async (
  data: IGetSubmissionsQuizHistory
) => {
  const _path = `${ApiPaths.submissions}/quizHistory/${data.quizId}?quizId=${data.quizId}&limit=${data.limit}&offset=${data.offset}`;

  const response = await apiDataProvider.get<
    TApiResponse<{
      limit: number;
      offset: number;
      totalDocs: number;
      docs: Array<ISubmission>;
    }>
  >(_path);

  return response.data;
};

const createSubmissionApi = async (data: ICreateNewSubmission) => {
  const response = await apiDataProvider.post<
    TApiResponse<{
      submission: ISubmission;
      subTopic: ISubTopic;
    }>
  >(ApiPaths.submissions, data);

  return response.data;
};

export const createNewSubmissionByQuizId = createAsyncThunk(
  'user/submission/createNewSubmissionByQuizId',
  async (data: ICreateNewSubmissionByQuizId) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        submission: ISubmission;
        subTopic: ISubTopic;
      }>
    >(`${ApiPaths.submissions}/createByQuizId`, data);

    return response.data;
  }
);

export const getSubmissionById = createAsyncThunk(
  'user/submission/id',
  async (id: string) => {
    const response = await apiDataProvider.get<TApiResponse<ISubmission>>(
      `${ApiPaths.submissions}/${id}`
    );

    return response.data;
  }
);

export const createSubmissionAnswer = createAsyncThunk(
  'user/submission/create/answer',
  async (data: ISubmissionCreateAnswer) => {
    const response = await apiDataProvider.post<TApiResponse<ISubmission>>(
      `${ApiPaths.submissions}/answer/add`,
      data
    );

    return response.data;
  }
);

export const finishSubmission = createAsyncThunk(
  'user/submission/finish',
  async (data: ISubmissionFinish) => {
    const response = await apiDataProvider.post<TApiResponse<ISubmission>>(
      `${ApiPaths.submissions}/finish`,
      data
    );

    return response.data;
  }
);

export const getSubmissionQuestion = createAsyncThunk(
  'user/submission/question/get',
  async (data: ISubmissionGetQuestion) => {
    const response = await apiDataProvider.post<TApiResponse<IQuestion>>(
      `${ApiPaths.submissions}/question/get`,
      data
    );
    return response.data;
  }
);

export const getSubmissionQuestionAIExplanation = createAsyncThunk(
  'user/submission/question/get-ai-explanation',
  async (data: ISubmissionGetQuestion) => {
    const response = await apiDataProvider.post<TApiResponse<string>>(
      `${ApiPaths.submissions}/question/get-ai-explanation`,
      data
    );

    return response.data;
  }
);

export const showSubmissionQuestionAnswer = createAsyncThunk(
  'user/submission/question/answer/get',
  async (data: ISubmissionGetQuestionAnswer) => {
    const response = await apiDataProvider.post<TApiResponse<IQuestion>>(
      `${ApiPaths.submissions}/answer/get`,
      data
    );

    return response.data;
  }
);

export const showAiHint = createAsyncThunk(
  'user/submission/question/aiHint',
  async (data: IGetAiHint) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        questionId: string;
        aiHint: string;
      }>
    >(`${ApiPaths.submissions}/aiHint`, data);

    return response.data;
  }
);

export const updateSubmissionCountdownSettings = createAsyncThunk(
  'user/submission/updateCountDownSettings',
  async (data: ISubmissionUpdateCountdownSettings) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        isTimerEnabled: boolean;
        rememberOption: boolean;
        timerStart?: number;
      }>
    >(`${ApiPaths.submissions}/updateSubmissionCountdownSettings`, data);

    return response.data;
  }
);

export const startSubmissionQuestionTimer = createAsyncThunk(
  'user/submission/startSubmissionQuestionTimer',
  async (data: IUpdateQuestionTimer) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        submissionId?: string;
        questionId?: string;
        timerStart?: number;
      }>
    >(`${ApiPaths.submissions}/startSubmissionQuestionTimer`, data);

    return response.data;
  }
);

export const endSubmissionQuestionTimer = createAsyncThunk(
  'user/submission/endSubmissionQuestionTimer',
  async (data: IUpdateQuestionTimer) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        submissionId?: string;
        questionId?: string;
        timerEnd?: number;
      }>
    >(`${ApiPaths.submissions}/endSubmissionQuestionTimer`, data);

    return response.data;
  }
);

export const updateSubmissionQuestionOpenAiFeedback = createAsyncThunk(
  'user/submission/updateQuestionOpenAiFeedback',
  async (data: IUpdateSubmissionQuestionOpenAiFeedback) => {
    const response = await apiDataProvider.post<
      TApiResponse<IUpdateSubmissionQuestionOpenAiFeedback>
    >(`${ApiPaths.submissions}/updateQuestionOpenAiFeedback`, data);

    return response.data;
  }
);

export const createSubmissionOpenEndedAnswer = createAsyncThunk(
  'user/submission/create/openEndedAnswer',
  async (data: ISubmissionCreateOpenEndedAnswer) => {
    const response = await apiDataProvider.post<TApiResponse<ISubmission>>(
      `${ApiPaths.submissions}/openEndedAnswer/add`,
      data
    );

    return response.data;
  }
);

export const gradingSubmissionOpenEndedAnswer = createAsyncThunk(
  'user/submission/grade/openEndedAnswer',
  async (data: ISubmissionGradeOpenEndedAnswer) => {
    const response = await apiDataProvider.post<TApiResponse<ISubmission>>(
      `${ApiPaths.submissions}/openEndedAnswer/grade`,
      data
    );

    return response.data;
  }
);

export const getQuizDataForAboutPage = createAsyncThunk(
  'user/submission/getQuizDataForAboutPage',
  async (data: { subTopicId: string }) => {
    const response = await apiDataProvider.post<
      TApiResponse<{
        list: Array<{ paper: string; quiz: IQuiz }>;
      }>
    >(`${ApiPaths.submissions}/getQuizDataForAboutPage`, data);

    return response.data;
  }
);

export const getAiFeedback = createAsyncThunk(
  'user/submission/getAiFeedback',
  async (data: { submissionId: string }) => {
    const response = await apiDataProvider.get<TApiResponse<any>>(
      `${ApiPaths.submissions}/get-ai-feedback/${data.submissionId}`
    );

    return response.data;
  }
);

export const getSubmissionList = createAsyncThunk(
  'user/submission/list',
  getSubmissionListModule
);

export const loadMoreSubmissionList = createAsyncThunk(
  'user/submission/loadMore',
  getSubmissionListModule
);

export const createNewSubmissionBySubTopic = createAsyncThunk(
  'user/submission/subTopic/createNewSubmissionBySubTopic',
  createSubmissionApi
);

export const getSubmissionsForQuizHistory = createAsyncThunk(
  'user/submission/quizHistory',
  getSubmissionsForQuizHistoryListModule
);

export const loadMoreSubmissionsForQuizHistory = createAsyncThunk(
  'user/submission/quizHistory/loadMore',
  getSubmissionsForQuizHistoryListModule
);

export const addSubmissionSubQuestionAnswer = createAsyncThunk(
  'user/submission/create/subQuestionAnswer',
  async (data: ISubmissionCreateSubQuestionAnswer) => {
    const response = await apiDataProvider.post<TApiResponse<ISubmission>>(
      `${ApiPaths.submissions}/subQuestionAnswer/add`,
      data
    );

    return response.data;
  }
);
